import React, { useMemo, useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import AppBar from '@groovehq/internal-design-system/lib/components/AppBar/AppBar'
import useExpiryEvent from 'subapps/chat/hooks/useExpiryEvent'
import styles from './styles.less'

const timeLabel = (mins, text) => {
  const rounded = Math.ceil(mins)
  return `${rounded} ${text}${rounded > 1 ? 's' : ''}`
}

const ExpirationStatusBarView = () => {
  const { isExpirable, remainingMins } = useExpiryEvent()
  const prevRemainingMinsRef = useRef()
  const [showAnimation, setShowAnimation] = useState(false)

  const expiryMessage = useMemo(
    () => {
      if (remainingMins > 24 * 60) {
        return {
          type: 'positive',
          title: `Remaining reply time: < ${timeLabel(
            remainingMins / 60 / 24,
            'day'
          )}`,
          description:
            'Messenger automatically closes conversations with no new replies within 7 Days.',
        }
      }

      if (remainingMins > 1 * 60) {
        return {
          type: 'warning',
          title: `Remaining reply time: < ${timeLabel(
            remainingMins / 60,
            'hour'
          )}`,
          description:
            'Messenger automatically closes conversations with no initial response time within 24 Hours.',
        }
      }

      if (remainingMins > 1) {
        return {
          type: 'negative',
          title: `Remaining reply time: < ${timeLabel(
            remainingMins,
            'minute'
          )}`,
          description:
            'Messenger automatically closes conversations with no initial response time within 24 Hours.',
        }
      }

      return {
        type: 'negative',
        title: 'Conversation closed by Messenger',
        description:
          'Messenger automatically closes conversations with no messages within 7 days.',
      }
    },
    [remainingMins]
  )

  useEffect(
    () => {
      let animationTimer

      if (
        prevRemainingMinsRef?.current &&
        prevRemainingMinsRef.current !== remainingMins
      ) {
        setShowAnimation(true)
        animationTimer = setTimeout(() => setShowAnimation(false), 2000)
      }

      prevRemainingMinsRef.current = remainingMins

      return () => {
        if (animationTimer) clearTimeout(animationTimer)
      }
    },
    [remainingMins]
  )

  if (!isExpirable) return null

  return (
    <AppBar
      type={expiryMessage.type}
      closable={false}
      close={false}
      className={cn(styles.wrapper, styles[expiryMessage.type], {
        appBarFadeOut: showAnimation,
        appBarFadeIn: !showAnimation,
      })}
    >
      <span className={styles.title}>{expiryMessage.title}</span>
      <br />
      <span>{expiryMessage.description}</span>{' '}
      <a
        className={styles.link}
        href="https://help.groovehq.com/help/messenger-reply-times"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more.
      </a>
    </AppBar>
  )
}

export default ExpirationStatusBarView
